/**
 * The stored-copy editor (#1911).
 *
 * ── THE PLACEHOLDER CHECK RUNS BEFORE THE SAVE, NOT AFTER IT ────────────────
 * A placeholder is substituted at send time and a send is unrecallable, so a
 * mistyped `{{ balnce }}` that gets stored reaches a customer's inbox with the
 * braces still in it. The route refuses it (422
 * `EMAIL_TEMPLATE_UNKNOWN_PLACEHOLDER`) and that refusal is the real gate —
 * this one exists because the server's answer arrives after the operator has
 * stopped looking at the word they got wrong. The allowed set is rendered
 * beside the fields for the same reason: "which words may I use" is the
 * question the error is answering, and it is cheaper to answer it first.
 *
 * ── SAVING A `balance_alert.*` ROW IS NOT LIKE SAVING A `notice.*` ROW ──────
 * The three alert rows are what an unattended cron mails at the moment nobody
 * is watching; the two notice rows are only ever a starting draft a human then
 * edits. Same form, very different blast radius — so the drawer says which one
 * this is rather than leaving it to be inferred from the key.
 */
import { useEffect, useState } from 'react';
import type { EmailTemplate } from '@nadoutong/contracts';
import { EMAIL_TEMPLATE_PLACEHOLDERS, unknownPlaceholders } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, Drawer, FormField, Input, Textarea } from '../../ui/index.ts';
import { templateHintKey, templateNameKey } from './templateNames.ts';

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly template: EmailTemplate | null;
  readonly saving: boolean;
  readonly testSending: boolean;
  readonly onSave: (subject: string, body: string) => void;
  readonly onTestSend: (to: string) => void;
}

export function EmailTemplateDrawer({
  open,
  onOpenChange,
  template,
  saving,
  testSending,
  onSave,
  onTestSend,
}: Props): JSX.Element {
  const t = useT();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [testTo, setTestTo] = useState('');

  const key = template?.key ?? null;
  // Seeded when the drawer OPENS or the key changes, and never on the stored
  // values themselves. React Query refetches this list on window focus, so a
  // dependency on `template.body` would replace whatever the operator has been
  // typing the moment they tab back from their mail client — with the copy
  // they were in the middle of replacing, and no indication it happened.
  useEffect(() => {
    setSubject(template?.subject ?? '');
    setBody(template?.body ?? '');
  }, [key, open]);

  const allowed = key === null ? [] : EMAIL_TEMPLATE_PLACEHOLDERS[key];
  const unknown = unknownPlaceholders(subject, body, allowed);
  const blank = subject.trim() === '' || body.trim() === '';
  const canSave = key !== null && !blank && unknown.length === 0;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      width="lg"
      title={key === null ? t('email_templates.drawer.title_none') : t(templateNameKey(key))}
      description={key ?? ''}
      closeLabel={t('common.close')}
      testId="email-template-drawer"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
          <Button
            loading={saving}
            disabled={!canSave}
            onClick={() => onSave(subject, body)}
            data-testid="email-template-save"
          >
            {t('email_templates.save')}
          </Button>
        </>
      }
    >
      {key === null ? null : (
        <div className="flex flex-col gap-4">
          <p
            className="m-0 rounded border border-line-soft bg-well px-2.5 py-2 text-dense text-paper-mid"
            data-testid="email-template-scope"
          >
            {t(templateHintKey(key))}
          </p>

          <div className="flex flex-col gap-1.5">
            <p className="m-0 text-label text-paper-mid">{t('email_templates.allowed_label')}</p>
            <div className="flex flex-wrap gap-1.5" data-testid="email-template-allowed">
              {allowed.map((name) => (
                <Chip key={name} tone="neutral" lamp={false}>{`{{ ${name} }}`}</Chip>
              ))}
            </div>
            <p className="m-0 text-micro text-paper-low">{t('email_templates.allowed_hint')}</p>
          </div>

          <FormField
            label={t('email_templates.field.subject')}
            fieldId="template-subject"
            required
          >
            <Input
              id="template-subject"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              data-testid="email-template-subject"
            />
          </FormField>

          <FormField
            label={t('email_templates.field.body')}
            fieldId="template-body"
            required
            error={
              unknown.length === 0
                ? null
                : t('email_notices.placeholder_unknown', { names: unknown.join(', ') })
            }
          >
            <Textarea
              id="template-body"
              rows={12}
              value={body}
              aria-describedby={unknown.length === 0 ? undefined : 'template-body-error'}
              onChange={(event) => setBody(event.target.value)}
              data-testid="email-template-body"
            />
          </FormField>

          <div className="flex flex-col gap-2 rounded border border-line-soft bg-well px-3 py-2.5">
            <p className="m-0 text-label text-paper-mid">{t('email_templates.test.caption')}</p>
            <p className="m-0 text-micro text-paper-low">{t('email_templates.test.hint')}</p>
            <FormField label={t('email_templates.test.to')} fieldId="template-test-to">
              <Input
                id="template-test-to"
                type="email"
                mono
                value={testTo}
                onChange={(event) => setTestTo(event.target.value)}
                data-testid="email-template-test-to"
              />
            </FormField>
            <div>
              <Button
                size="sm"
                variant="secondary"
                loading={testSending}
                disabled={!testTo.includes('@')}
                onClick={() => onTestSend(testTo)}
                data-testid="email-template-test-send"
              >
                {t('email_templates.test.action')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
}
